import { useRouter } from 'next/router';
import { useState } from 'react';

import { useMiniCart } from 'hooks/useMiniCart';
import {
  useModalsOpenStore,
  usePopupsOpenStore,
  useTempStateStore,
} from 'store';
import { getIsMobile } from 'utils/media';
import { ProductCartItem, Size, Variant } from '__generated__/graphql';
import { useCart } from 'hooks/useCart';

import { AddToCartButton } from '../../AddToCartButton';

export const GWPNew_AddToCartButton = ({
  selectedSize,
  selectedVariant,
  gwpId,
}: {
  selectedSize: Size | undefined;
  selectedVariant: Variant | undefined;
  gwpId: string;
}) => {
  const { openMiniCart } = useMiniCart();
  const { addToCart } = useCart();
  const { setModalOpen } = useModalsOpenStore();
  const { setPopupOpen } = usePopupsOpenStore();
  const router = useRouter();
  const { getTempState } = useTempStateStore();

  const [addingToCart, setAddingToCart] = useState(false);

  const isCartPage = router.asPath.includes('/cart');

  const onAddToCart = async () => {
    if (!selectedSize || !selectedVariant) return null;

    setAddingToCart(true);
    const addItemsToCart = (
      await addToCart(
        {
          productId: selectedSize?.productId as string,
          bonusItemId: gwpId,
          quantity: 1, // only allow add 1 for now
          swatch: selectedVariant.colorValue,
          masterId: selectedVariant.id.split('_')[0],
        },
        undefined, // [additionalGAProductData]
        // NOTE:  inside this function the miniCart opens only with the last product added,
        //        so we don't want to open the miniCart from here because we want to open
        //        the miniCart with 2 products (original item + gwp item)
        false, // [showMiniCart]
        router.asPath
      )
    ).data?.addItemsToCart;
    setAddingToCart(false);

    setModalOpen('GWPModal', false);

    // don't try to open popup or miniCart when user is on Cart page
    if (!isCartPage) {
      if (getIsMobile()) {
        // in mobile open popUp
        setPopupOpen('CartButtonPopup', true);
      } else {
        // in desktop open miniCart with the last product added to cart + gwp product

        const cartProducts = addItemsToCart?.products as ProductCartItem[];
        // NOTE:  we need to use the `getTempState('IdOfLastProductAddedToCart')` function
        //        to filter the product in the cart by this id, because when we add the
        //        same product in the cart for the 2nd time and with another product in
        //        between, we only are updating the quantity field and no "adding" new item
        //        to the array of product objects. So we cannot get the last item of the
        //        cart by trying to get the last item of the array.
        const lastProductAddedToTheCart = cartProducts?.find(
          p => p.product.id === getTempState('IdOfLastProductAddedToCart')
        );
        const gwpProduct = cartProducts?.find(p => p.bonusItemId === gwpId);

        if (lastProductAddedToTheCart && gwpProduct) {
          openMiniCart([lastProductAddedToTheCart, gwpProduct], true);
        }
      }
    }
  };

  return (
    <AddToCartButton
      disabled={!selectedSize?.orderable || !selectedVariant}
      isAppExclusive={selectedVariant?.isAppExclusive}
      loading={addingToCart}
      onAddToCart={onAddToCart}
      presale={selectedVariant?.displayOutOfStock?.presale}
      size="lg"
      appOnlyDateTimeFrom={selectedVariant?.appOnlyDateTimeFrom}
      appOnlyDateTimeTo={selectedVariant?.appOnlyDateTimeTo}
    />
  );
};
